import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import RoundedButton from "@/components/rounded-button"
import Topbar from "@/components/topbar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Icon } from "@/icons/icon"
import { FormParamStatus, FormStatus } from "@/lib/form/types"
import { cn } from "@/lib/utils"
import {
  Sheet as ShadSheet,
  SheetContent as ShadSheetContent,
  SheetHeader as ShadSheetHeader,
  SheetTitle as ShadSheetTitle,
} from "@/components/ui/sheet"
import {
  syncCurrentOfflineBoiloutSession,
  syncSubmittedOfflineBoiloutHandovers,
} from "@/services/boilout"
import { getShiftStatus, mergeSession } from "@/services/session"
import useStore from "@/store"
import { ShiftDialog } from "@/store/boilout"
import { ShiftStatus } from "@/store/session"
import { ReactNode, useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { AlertCircle, Check, Loader2 } from "lucide-react"
import { toast } from "sonner"

export type HeaderSyncStatus = "idle" | "saving" | "saved" | "error"

export type ProductFlowLayoutProps = {
  className?: string
  children?: ReactNode
  hideBottomActions?: boolean
  isSubmitDisabled?: boolean
  isDeleteHidden?: boolean
  status?: FormStatus
  canAddProducts?: boolean
  hideEndProcess?: boolean
  headerSyncStatus?: HeaderSyncStatus
  onClickDelete?: () => void
  onClickSubmit?: () => void
  onClickEndShift?: () => void
  onClickDiscardShift?: () => void
}

export default function ProductFlowLayout({
  className,
  children,
  hideBottomActions,
  isSubmitDisabled,
  canAddProducts,
  isDeleteHidden,
  status = FormStatus.DRAFT,
  hideEndProcess,
  headerSyncStatus = "idle",
  onClickDelete,
  onClickSubmit,
  onClickEndShift,
  onClickDiscardShift,
}: ProductFlowLayoutProps) {
  const navigate = useNavigate()
  const setShiftDialog = useStore.use.boilout().setShiftDialog
  const { plant } = useStore((state) => state.user)
  const sessionStore = useStore.use.session()
  const isSubmitLoading = useStore((state) => state.boilout.submitLoading)
  const currProdId = useStore.use.boilout().currProdId()
  const currHandoverSessionId = useStore.use.boilout().currHandoverSessionId
  const histories = useStore.use.boilout().histories
  const processSession = useStore.use.boilout().shift
  const offlineActiveSession = useStore.use.boilout().offline.activeSession
  const getProductHistory = useStore.use.boilout().getProductHistory
  const isPrevHistoryShown = useShowPrevHistory()
  const [showResyncSheet, setShowResyncSheet] = useState(false)
  const [isResyncing, setIsResyncing] = useState(false)

  function useShowPrevHistory() {
    const [show, setShow] = useState(false)

    useEffect(() => {
      if (getProductHistory(currProdId)?.productionLines.length) {
        setShow(true)
      } else {
        setShow(false)
      }
    }, [currProdId, currHandoverSessionId, histories])

    return show
  }

  const isSubmittedStatus =
    status === FormParamStatus.SUBMITTED ||
    status === FormParamStatus.COMPLETED ||
    status === FormParamStatus.HANDOVER_SUBMITTED

  const isCompletedStatus = status === FormParamStatus.COMPLETED

  async function handleResync() {
    try {
      setIsResyncing(true)
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setShowResyncSheet(false)
        toast.error("Reconnect to the internet before resyncing data")
        return
      }

      const activeSession = useStore.getState().boilout.offline.activeSession

      if (activeSession?.pendingFinalizeAt) {
        setShowResyncSheet(false)
        toast.error("Finish syncing the ended Boilout process before resyncing")
        return
      }

      if (activeSession && (activeSession.isDirty || activeSession.hasDeferredSync)) {
        await syncSubmittedOfflineBoiloutHandovers()
        await syncCurrentOfflineBoiloutSession()
      }

      setShowResyncSheet(false)
      toast.success("Resync complete")
      await navigate("/manage-processes")
    } catch (err) {
      console.error("Failed to resync Boilout data:", err)
      toast.error("Failed to resync data")
    } finally {
      setIsResyncing(false)
    }
  }

  function getHeaderSyncIndicator() {
    if (headerSyncStatus === "idle") {
      return null
    }

    return (
      <div
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-full border bg-white",
          headerSyncStatus === "saving" && "border-blue/30 text-blue",
          headerSyncStatus === "saved" && "border-success/30 text-success",
          headerSyncStatus === "error" && "border-error/30 text-error"
        )}
        role="status"
        aria-live="polite"
      >
        {headerSyncStatus === "saving" && (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span className="sr-only">Syncing</span>
          </>
        )}
        {headerSyncStatus === "saved" && (
          <>
            <Check className="h-3.5 w-3.5 animate-pulse" />
            <span className="sr-only">Synced</span>
          </>
        )}
        {headerSyncStatus === "error" && (
          <>
            <AlertCircle className="h-3.5 w-3.5 animate-pulse" />
            <span className="sr-only">Sync failed</span>
          </>
        )}
      </div>
    )
  }

  function getButtonContent() {
    if (isSubmittedStatus) {
      return (
        <span className="flex gap-2 items-center">
          <div>Submitted Successfully</div>
          <Icon name="CheckboxOutline" className="text-white h-6 w-auto" />
        </span>
      )
    }

    return "Submit"
  }

  function canEdit(): boolean {
    const process = sessionStore.session.processes.find(
      (proc) => proc.processId === processSession.processId
    )
    if (!process) {
      return false
    }

    return (
      process.status === ShiftStatus.Started ||
      process.status === ShiftStatus.Draft
    )
  }

  function handleClickAddProducts() {
    setShiftDialog(ShiftDialog.AddProducts)
  }

  async function handleClickViewPrevHistory() {
    await navigate(`/boilout/histories/${currProdId}`)
  }

  async function handleClickBack() {
    const isOffline =
      typeof navigator !== "undefined" && navigator.onLine === false

    if (!offlineActiveSession && !isOffline) {
      void getShiftStatus()
        .then((res) => {
          if (res.shiftSession) {
            mergeSession(res.shiftSession)
          }
        })
        .catch((err) => {
          console.warn("Failed to refresh shift status before leaving BOILOUT", err)
        })
    }

    await navigate("/manage-processes")
  }

  return (
    <div className="h-dvh overflow-auto">
      <Topbar
        className="sticky top-0 z-10"
        title="BOILOUT Product"
        subtitle={plant.name}
        titleSlot={getHeaderSyncIndicator()}
        showBack
        onClickBack={handleClickBack}
      >
        <div className="flex items-center gap-5">
          {canAddProducts && canEdit() && (
            <RoundedButton
              icon={<Icon name="Plus" />}
              onClick={handleClickAddProducts}
            >
              Product
            </RoundedButton>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Icon name="MoreVertical" className="text-button-black h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent inPortal>
              {isPrevHistoryShown && (
                <DropdownMenuItem onClick={handleClickViewPrevHistory}>
                  View Previous History
                </DropdownMenuItem>
              )}

              {canEdit() && (
                <>
                  {!hideEndProcess && (
                    <DropdownMenuItem onClick={onClickEndShift}>
                      End Process
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={onClickDiscardShift}>
                    Discard and Exit
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={() => setShowResyncSheet(true)}>
                Resync
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Topbar>

      <div
        className={cn(
          isCompletedStatus || (!canEdit() && isSubmittedStatus)
            ? "pb-4"
            : "pb-22",
          className
        )}
      >
        {children}
      </div>

      {!hideBottomActions &&
        !isCompletedStatus &&
        (canEdit() || !isSubmittedStatus) && (
        <Bottombar className="fixed bottom-0 left-1/2 w-full max-w-md -translate-x-1/2 flex gap-4">
          {!isSubmittedStatus && !isDeleteHidden && (
            <Button
              variant="outline"
              disabled={isSubmitLoading}
              onClick={onClickDelete}
            >
              Delete
            </Button>
          )}
          <Button
            className="grow"
            color={isSubmittedStatus ? "success" : undefined}
            disabled={isSubmitDisabled || isSubmittedStatus}
            isLoading={isSubmitLoading}
            onClick={onClickSubmit}
          >
            {getButtonContent()}
          </Button>
        </Bottombar>
      )}

      <ShadSheet
        open={showResyncSheet}
        onOpenChange={(open) => {
          if (!isResyncing) {
            setShowResyncSheet(open)
          }
        }}
      >
        <ShadSheetContent
          side="bottom"
          className="shadow-drop-shadow mx-auto rounded-t-lg"
        >
          <ShadSheetHeader className="pb-0 sticky top-0 bg-white">
            <ShadSheetTitle className="text-xl font-semibold flex flex-col gap-5">
              <div className="h-13 w-13 flex items-center justify-center rounded-full bg-backdrop">
                <Icon name="History" className="h-6 w-auto" />
              </div>

              <div>Resync Data</div>
            </ShadSheetTitle>
          </ShadSheetHeader>

          <div className="font-normal pt-4 px-4 py-4">
            This will sync pending local data when possible, then refresh the
            page from the server. Reconnect to the internet before using
            resync.
          </div>

          <Bottombar className={cn("flex gap-4 bg-white sticky bottom-0")}>
            <Button
              variant="outline"
              onClick={() => setShowResyncSheet(false)}
              disabled={isResyncing}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              variant="destructive"
              disabled={isResyncing}
              isLoading={isResyncing}
              onClick={() => void handleResync()}
            >
              {isResyncing ? "Resyncing..." : "Resync"}
            </Button>
          </Bottombar>
        </ShadSheetContent>
      </ShadSheet>
    </div>
  )
}
